import { Injectable } from '@angular/core';
import { Login } from '../../models/login';
import { ServiceImpl } from './service.impl';
import { IAuthenticationService } from '../authentication.service.interface';
import { HttpClient } from '@angular/common/http';
import { LocalStorageHelper } from '../../helpers/local.storage.helper';
import { LOCAL_STORAGE_CONSTANTS } from '../../constants/local.storage.constants';
import { Router } from '@angular/router';
import SessionToken from '../../models/session-token.model';
import { ROUTES_CONST } from '../../constants/routes.constants';
import User from '../../models/user.model';
import LoginHistory from '../../models/login-history.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ChangePassword } from 'src/app/core/models/change-password.model';
import { ChangePasswordResponse } from 'src/app/core/models/change-password-response.model';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
const AUTH_URL = {
	LOGIN: 'login',
	LOGOUT: 'logout',
	USER_INFO: 'user',
	LOGIN_HISTORY: 'loginHistory',
	CHANGE_PASS: 'change-password'
};

@Injectable({
  providedIn: 'root'
})
export class AuthenticationServiceImpl extends ServiceImpl<Login> implements IAuthenticationService {

	constructor(
		public  http: HttpClient,
		public  localStorageHelper: LocalStorageHelper,
		public  router: Router,
		public  jwtHelper: JwtHelperService
	) {
		super();
		this.httpClient = http;
		this.resource = 'security-user/';
		this.apiUrl = environment.apiUrl;
	}

	login(login: Login) {
		const token = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.TOKEN)
		return this.httpClient.post(`${this.getFullPath()}usuario/login`, login);
	}

	async loadSessionInfo(token: any, infoUser: any) {
		try {
			const permisos: any = await this.httpClient.get(`${this.getFullPath()}usuario/permisos/${infoUser.username}`).toPromise()
			const permissions: Array<string> = String(permisos.data).split(' ');
			let session = new SessionToken();
			session = {
				token,
				permissions,
				name: infoUser.name,
				expiresIn: token.expires_in,
				lastLogin: infoUser.previous_login,
				username: infoUser.username,
				email: infoUser.email
			};
			if (token.hasOwnProperty('oauth')) {
				session['oauth'] = true;
			} else {
				session['oauth'] = false;
			}
			if (token.hasOwnProperty('isTeacher')) {
				session['isTeacher'] = true;
			} else {
				session['isTeacher'] = false;
			}
			if (token.hasOwnProperty('key')) {
				session.key = token.key;
			}
			this.initSession(session);
		} catch (e) {
			// console.log(e);
		}
	}

	logout(): Observable<SessionToken> {
		return this.post(this.getSessionToken(), AUTH_URL.LOGOUT)
			.pipe(
				map((res: any) => <SessionToken>res)
			);
	}

	getUserInfo(): Observable<User> {
		return this.post(this.getSessionToken(), AUTH_URL.USER_INFO).pipe(
			map((res: any) => <User>res)
		);
	}

	getLoginHistory(): Observable<Array<LoginHistory>> {
		return this.post(this.getSessionToken(), AUTH_URL.LOGIN_HISTORY).pipe(
			map((res: any) => <Array<LoginHistory>>res)
		);
	}

	closeSession(): void {
		const sessionToken = this.getSessionToken();
		let url = ROUTES_CONST.LOGIN.routerLink;
		if (sessionToken.oauth) {
			url = '/'
		}
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.TOKEN);
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.PERMISOS);
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.LAST_LOGIN);
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.NAME);
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.EXPIRE_IN);
		this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.USERNAME);
    localStorage.clear();
		this.router.navigateByUrl('auth/login');
	}

	saveToken(token: any) {
		const infoUser = this.jwtHelper.decodeToken(token.access_token);
		this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.TOKEN, token.access_token);
		this.loadSessionInfo(token, infoUser);
	}

	initSession(sessionToken: SessionToken): void {
		let redirectUrl;
		if (sessionToken.key) {
			this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.SESSION_TEMP, JSON.stringify(sessionToken));
			redirectUrl = ROUTES_CONST.CONFIRM_PSWD.routerLink;
		} else {
			if (!redirectUrl) {
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.PERMISOS, JSON.stringify(sessionToken.permissions));
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.LAST_LOGIN, String(sessionToken.lastLogin));
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.NAME, sessionToken.name);
				const expiresAt = moment().add(sessionToken.expiresIn, 'second');
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.EXPIRE_IN, JSON.stringify(expiresAt));
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.USERNAME, sessionToken.username);
				this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.EMAIL, sessionToken.email);
				this.router.navigate([ROUTES_CONST.HOME.routerLink]);
				if (sessionToken.oauth) {
					redirectUrl = ROUTES_CONST.CLASE_ZOOM.routerLink;
				} else if (sessionToken.isTeacher) {
					redirectUrl = ROUTES_CONST.DASHBOARD_ZOOM.routerLink;
				} else {
					redirectUrl = ROUTES_CONST.HOME.routerLink;
				}
			} else {
				this.localStorageHelper.removeItem(LOCAL_STORAGE_CONSTANTS.REDIRECT_URL);
			}
		}
		this.router.navigate([redirectUrl]);
	}

	addUserToSession(user: User): void {
		this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.USER, JSON.stringify(user));
	}

	getUserFromSession(): User {
		const user = new User();
		const lastLogin = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.LAST_LOGIN);
		user.lastLogin = moment(new Date(lastLogin)).format('DD-MMM-YYYY HH:mm:ss');
		user.name = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.NAME);
		user.email = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.EMAIL);
		return user;
	}

	getSessionToken(): SessionToken {
		const sessionToken = new SessionToken();
		sessionToken.token = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.TOKEN);
		return sessionToken;
	}

	getAvailableSedes(): Array<string> {
		return JSON.parse(this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.SEDES));
	}

	getSelectedSedes(): Array<string> {
		return JSON.parse(this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.SEDE_ACTUAL));
	}

	setSelectedSedes(sedes: Array<string>): void {
		return this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.SEDE_ACTUAL, JSON.stringify(sedes));
	}

	private getPermissions(): Array<string> {
		return JSON.parse(this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.PERMISOS)) ? JSON.parse(this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.PERMISOS)) : [];
	}

	hasAccess(action: string): boolean {
		if (!action) {
			return false;
		}
		const permissions = this.getPermissions() || '';
		const wildcardAction = action.split(':')[0] + ':*';
		return permissions.includes(action) || permissions.includes(wildcardAction);
	}

	oAuthUser(userOAuth: any) {
		const isValidTime = new Date().getTime() < userOAuth.exp
		if (!isValidTime) { return false }
		return this.httpClient.post(`${this.getFullPath()}usuario/login-oauth`, { username: userOAuth.email, tipoDispositivo: 'WEB' });
	}

	hasModule(module: string): boolean {
		if (!module) {
			return true;
		}
		const modules = this.getPermissions().map((permission: String) => permission.split(':')[0]);
		return modules.indexOf(module) > -1;
	}

	isValidSession(): boolean {
		try {
			const token = this.getSessionToken();
			const expiresIn = this.getExpiration();
			return token && moment().isBefore(expiresIn);
		} catch (e) {
			return false;
		}
	}

	public  getExpiration() {
		const expiration = JSON.parse(this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.EXPIRE_IN));
		return moment(expiration);
	}

	changePass(changePassword: ChangePassword): Observable<ChangePasswordResponse> {
		return this.httpClient.post(`${this.getFullPath()}usuario/cambio-clave`, changePassword).pipe(
			map((res: any) => <ChangePasswordResponse>res)
		);
	}

	resetPass(body: any) {
		return this.httpClient.put(`${this.getFullPath()}usuario/reset-clave`, body);
	}

	confirmarClave(email: any, password: any, passwordKey: any) {
		return this.httpClient.put(`${this.getFullPath()}usuario/confirmar-clave`, {
			email,
			password,
			passwordKey
		}).pipe(
			map((res: any) => res.data)
		);
	}

	getUserDashboard(): Array<string> {
		const dashboardPermisos = this.getPermissions().filter(
			(permission: string) => permission.includes(':dashboard')
		);
		return dashboardPermisos;
	}

	async refreshPermissions(): Promise<string[]> {
		const username = this.localStorageHelper.getItem(LOCAL_STORAGE_CONSTANTS.USERNAME);
		if (!username) {
			return [];
		}

		const permisos: any = await firstValueFrom(
			this.httpClient.get(`${this.getFullPath()}usuario/permisos/${username}`)
		);
		const permissions = String(permisos.data).split(' ').filter(Boolean);
		this.localStorageHelper.addItem(LOCAL_STORAGE_CONSTANTS.PERMISOS, JSON.stringify(permissions));
		return permissions;
	}

	findPermissionDocente() {
		const dashboardPermisos = this.getPermissions().filter(
			(permission: string) => permission.includes('select-docente:')
		);
		return dashboardPermisos.length ? dashboardPermisos : null;
	}

	findPermissionEstudiante() {
		const dashboardPermisos = this.getPermissions().filter(
			(permission: string) => permission.includes('estudiante:dashboard')
		);
		return dashboardPermisos.length > 0 ? true : false;
	}
}
